import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { ImageUploadField } from "@/components/admin/ImageUploadField";
import { CTA_TEMPLATES, createCTAFromTemplate } from "@/lib/ctaTemplates";
import { Plus, Trash2, Eye, EyeOff, ChevronUp, ChevronDown } from "lucide-react";

export const CTAManager = ({ ctas, onChange }) => {
  const [templateKey, setTemplateKey] = useState(CTA_TEMPLATES[0].key);

  const addCTA = () => onChange([...ctas, createCTAFromTemplate(templateKey)]);
  const updateCTA = (i, field, value) => {
    const next = [...ctas];
    next[i] = { ...next[i], [field]: value };
    onChange(next);
  };
  const removeCTA = (i) => onChange(ctas.filter((_, idx) => idx !== i));
  const move = (i, dir) => {
    const next = [...ctas];
    const target = i + dir;
    if (target < 0 || target >= next.length) return;
    [next[i], next[target]] = [next[target], next[i]];
    onChange(next);
  };

  return (
    <div data-testid="cta-manager" className="space-y-4">
      <div className="flex gap-2">
        <Select value={templateKey} onValueChange={setTemplateKey}>
          <SelectTrigger data-testid="cta-template-select" className="rounded-none flex-1"><SelectValue /></SelectTrigger>
          <SelectContent>
            {CTA_TEMPLATES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button type="button" onClick={addCTA} data-testid="cta-add-button" variant="outline" className="rounded-none shrink-0"><Plus className="h-4 w-4 mr-1" /> Add CTA</Button>
      </div>

      {ctas.map((cta, i) => (
        <div key={cta.id} data-testid={`cta-block-editor-${i}`} className="border border-border p-4 space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-xs uppercase tracking-wide text-muted-foreground">{CTA_TEMPLATES.find((t) => t.key === cta.template_key)?.label || "Custom"}</span>
            <div className="flex gap-1">
              <Button type="button" size="icon" variant="outline" className="rounded-none h-8 w-8" data-testid={`cta-move-up-${i}`} onClick={() => move(i, -1)}><ChevronUp className="h-4 w-4" /></Button>
              <Button type="button" size="icon" variant="outline" className="rounded-none h-8 w-8" data-testid={`cta-move-down-${i}`} onClick={() => move(i, 1)}><ChevronDown className="h-4 w-4" /></Button>
              <Button type="button" size="icon" variant="outline" className="rounded-none h-8 w-8" data-testid={`cta-toggle-visible-${i}`} onClick={() => updateCTA(i, "visible", !cta.visible)}>
                {cta.visible ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4 text-muted-foreground" />}
              </Button>
              <Button type="button" size="icon" variant="outline" className="rounded-none h-8 w-8" data-testid={`cta-delete-${i}`} onClick={() => removeCTA(i)}><Trash2 className="h-4 w-4" /></Button>
            </div>
          </div>
          <div><Label className="text-xs">Heading</Label><Input data-testid={`cta-heading-${i}`} className="rounded-none mt-1" value={cta.heading} onChange={(e) => updateCTA(i, "heading", e.target.value)} /></div>
          <div><Label className="text-xs">Description</Label><Textarea data-testid={`cta-description-${i}`} className="rounded-none mt-1" value={cta.description} onChange={(e) => updateCTA(i, "description", e.target.value)} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Button Text</Label><Input data-testid={`cta-button-text-${i}`} className="rounded-none mt-1" value={cta.button_text} onChange={(e) => updateCTA(i, "button_text", e.target.value)} /></div>
            <div><Label className="text-xs">Button Link</Label><Input data-testid={`cta-button-link-${i}`} className="rounded-none mt-1" value={cta.button_link} onChange={(e) => updateCTA(i, "button_link", e.target.value)} /></div>
          </div>
          <div>
            <Label className="text-xs">Background</Label>
            <div className="flex gap-2 mt-1">
              <Select value={cta.bg_type} onValueChange={(v) => updateCTA(i, "bg_type", v)}>
                <SelectTrigger data-testid={`cta-bg-type-${i}`} className="rounded-none w-32"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="color">Color</SelectItem><SelectItem value="image">Image</SelectItem></SelectContent>
              </Select>
              {cta.bg_type === "color" ? (
                <input type="color" data-testid={`cta-bg-color-${i}`} className="h-9 w-16 border border-border cursor-pointer" value={cta.bg_value?.startsWith("#") ? cta.bg_value : "#0F2922"} onChange={(e) => updateCTA(i, "bg_value", e.target.value)} />
              ) : (
                <div className="flex-1"><ImageUploadField testId={`cta-bg-image-${i}`} value={cta.bg_value} onChange={(v) => updateCTA(i, "bg_value", v)} /></div>
              )}
            </div>
          </div>
        </div>
      ))}
      {ctas.length === 0 && <p className="text-muted-foreground text-sm">No CTA sections added yet. Pick a template above and click "Add CTA".</p>}
    </div>
  );
};
